import type { MultiplierValue } from '@/store/slices/site-wide-jackpot';

export type AmplitudeGlobalKeys = {
    Product: 'casino_standalone_lobby' | 'casino_xsell_lobby' | 'poker_standalone_lobby' | undefined;
    'Page Name': 'menu' | 'search' | string;
    Balance: number | null;
    'Login Status': 'logged_in' | 'logged_out';
    'Site Version': 'mobile_web' | 'native' | 'desktop';
    'Site Platform': 'desktop' | 'mobile_web' | 'ios' | 'android';
    Jurisdiction: string; // TODO: what happens in case of invalid/default region?
    'National Jurisdiction': 'usa' | 'canada';
    // web sdk only
    'Full URL': string;
    Hostname: string;
    'Page Path': string;
    'Page Title': string;
    // native only
    'App Name': ''; // TODO: help
    'App ID': ''; // TODO: help
    'Android Distribution Method': 'side-load' | 'play-store' | '';
    'Next.js': boolean;
};

// collection of all possible event keys
export type AmplitudeEventKeys = {
    event:
        | 'Navigation Link Clicked'
        | 'Game Favorited'
        | 'Game Favorite Removed'
        | 'State Selector Modal Viewed'
        | 'State Selected'
        | 'State Selector CTA Clicked'
        | 'Region Switch Access Clicked'
        | 'Region Selected'
        | 'Game Info Modal Opened'
        | 'Game Information Page Viewed'
        | 'Category Page Viewed'
        | 'Epic Hub Page Viewed'
        | 'Influencer Hub Page Viewed'
        | 'Influencer Video Page Viewed'
        | 'Influencer Video Selected'
        | 'Influencer Video Auto Play Successful'
        | 'Influencer Video Milestone Played'
        | 'Game Selected'
        | 'Game Video Auto Played Successful'
        | 'Game Video 100pc Played'
        | 'Game Video Muted'
        | 'Game Video Unmuted'
        | 'Games Exclusive Launch Modal Opened'
        | 'Games Exclusive Launch Modal Closed'
        | 'Games Exclusive Launch CTA Clicked'
        | 'Game Reminder Enabled'
        | 'Game Reminder Disabled'
        | 'Search Page Viewed'
        | 'Search Started'
        | 'Search Initialized'
        | 'Search Results Returned'
        | 'Search Result Selected'
        | 'Category Game Info Link Clicked'
        | 'FDC Jackpots Intro Modal Viewed'
        | 'FDC Jackpots Intro Modal Closed'
        | 'FDC Jackpots Intro Modal CTA Clicked'
        | 'FDC DYB Intro Modal CTA Clicked'
        | 'FDC Jackpots Opt In On Toggled'
        | 'FDC Jackpots Opt In Off Toggled'
        | 'FDC Jackpots Modal Viewed'
        | 'FDC Jackpots Modal Closed'
        | 'FDC Jackpots Module CTA Clicked'
        | 'Game Collection Selected'
        | 'Game Banner Viewed'
        | 'Game Banner Clicked'
        | 'Promo Banner Viewed'
        | 'Promo Banner Clicked'
        | 'Category Banner Viewed'
        | 'Category Banner Clicked'
        | 'RAF Banner Viewed'
        | 'RAF Banner Clicked'
        | 'RM Welcome Modal Viewed'
        | 'RM Exit Modal Viewed'
        | 'RM More Info Modal Viewed'
        | `${string} Page Viewed`;
    Module: string;
    'Link Text': string;
    'Link Type': string;
    Region: string;
    'Event Category': string;
    'Event Label': string;
    'Link URL': string;
    Tag: string;
    'Banner Format': string;
    'Promo Name': string;
    'Full URL'?: string;
    Hostname?: string;

    Category: string;
    'Zone Name': string;
    'Zone Layout'?: string;
    'Zone Type'?: string;
    'Game Name': string;
    Position: string;
    'Zone Position': string;
    'Game ID': string;
    'Video Position': number;
    'Game Provider': string;
    'Third Party Lobby': string;
    'Is Favorite': string;
    'Is Jackpot': string;
    'Jackpot Type': string;
    'FDC Jackpot Multiplier': string;
    'FDC Jackpot Opt In Status': string;
    'Number Of Actives': string;
    'FDC Jackpot Mega Pot Amount': number;
    'FDC Jackpot Major Pot Amount': number;
    'FDC Jackpot Minor Pot Amount': number;
    'FDC Jackpot Mini Pot Amount': number;
    Multiplier: MultiplierValue;
    OptInStatus: string;
    'Page Name': string;
    'Page Path'?: string;
    'Navigation Source': string;
    'Is Zone Dynamically Ordered': boolean;
    'Dynamic Ordering Active'?: 'static' | 'dynamic';
    'Game Tile Type'?: 'static' | 'animated';
    'Animated Tile Count'?: number;
    'Interaction Type': string;
    'Search Keyword': string;
    'Raw Search Keyword': string;
    'Search Results': number;
    Source: string;
    'Number of Actives': string;
    'Promo ID'?: string;
    'Game Collection Name'?: string;
    Personalized: string | null;
    'Pinned Category': string | null;
    'Is Locked'?: boolean;
    'Reward Box Type'?: string;
    'Unlock Timestamp'?: string;
    'Countdown Duration'?: number;
    'Video Title'?: string;
    'Video ID'?: string;
    'Total Video Duration'?: number;
    Percentage?: 25 | 50 | 75 | 100;
};

const AMPLITUDE_GLOBAL_PROPERTIES_MAP = {
    brand: 'Brand',
    fullUrl: 'Full URL',
    hostname: 'Hostname',
    loginStatus: 'Login Status',
    nationalJurisdiction: 'National Jurisdiction',
    pageName: 'Page Name',
    pagePath: 'Page Path',
    pageTitle: 'Page Title',
    routeType: 'Route Type',
    product: 'Product',
    source: 'Source',
    sitePlatform: 'Site Platform',
    siteVersion: 'Site Version',
    jurisdiction: 'Jurisdiction',
    androidDistributionMethod: 'Android Distribution Method',
    category: 'Category',
    gameProvider: 'Game Provider'
};

const AMPLITUDE_EVENT_PROPERTIES_MAP = {
    module: 'Module',
    linkText: 'Link Text',
    eventCategory: 'Event Category',
    linkUrl: 'Link URL',
    tag: 'Tag',
    zoneName: 'Zone Name',
    region: 'Region'
};

const AMPLITUDE_USER_PROPERTIES_MAP = {
    balance: 'Balance',
    jurisdiction: 'Jurisdiction',
    loginStatus: 'Login Status',
    sitePlatform: 'Site Platform',
    siteVersion: 'Site Version',
    userLocation: 'User Location',
    stateSelected: 'State Selected'
};

const AMPLITUDE_REWARDS_PROPERTIES_MAP = {
    rewardId: 'Reward ID',
    rewardType: 'Reward Type',
    rewardName: 'Reward Name',
    errorReason: 'Error Reason',
    animationState: 'Animation State',
    interactionType: 'Interaction Type',
    rewardAmount: 'Reward Amount',
    position: 'Position',
    rewardsCount: 'Rewards Count',
    casinoBonusAmount: 'Casino Bonus Dollar Amount',
    bonusSpinsAmount: 'Casino Bonus Spins Amount',
    isLocked: 'Is Locked',
    rewardBoxType: 'Reward Box Type',
    unlockTimestamp: 'Unlock Timestamp',
    countdownDuration: 'Countdown Duration'
};

export const AMPLITUDE_PROPERTIES_MAP = {
    ...AMPLITUDE_GLOBAL_PROPERTIES_MAP,
    ...AMPLITUDE_USER_PROPERTIES_MAP,
    ...AMPLITUDE_EVENT_PROPERTIES_MAP,
    ...AMPLITUDE_REWARDS_PROPERTIES_MAP
};
export type AmplitudePropertiesMap = typeof AMPLITUDE_PROPERTIES_MAP;

// ------------------------------------------------------------
export type EventGlobalProperties = Partial<Record<keyof typeof AMPLITUDE_PROPERTIES_MAP, string>>;
